import dayjs from 'dayjs'
import {
  StoreType,
  PreOrderSettings,
  CutoffSchedule,
  OperatingSchedules,
  Order
} from './types'
import { Timeslot } from './FulfillmentTimeSlot'

const DATE_FORMAT = 'YYYY-MM-DD'

/*
  dayjs has no equivalent to moment.parseZone: given an ISO string with an
  embedded UTC offset, this reads its wall-clock time as written instead of
  converting it to the browser's local offset.
*/
export const formatInWrittenOffset = (isoDateTime: string, format: string) =>
  dayjs(isoDateTime.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')).format(format)

export const disabledDate = (
  date: Date,
  currentDate: Date,
  preOrderSettings: PreOrderSettings,
  order: Partial<Order>
) => {
  const isBeforeToday = dayjs(date).isBefore(dayjs(currentDate).startOf('day'))

  if (isBeforeToday) return true

  const withinOperatingSchedule = order.is_preorder
    ? isDateValidForWarehouse(date, preOrderSettings)
    : true

  return !withinOperatingSchedule
}

const isDateValidForWarehouse = (
  date: Date,
  preOrderSettings: PreOrderSettings
) => {
  if (preOrderSettings?.cutoffs) {
    const operatingDays = Object.keys(preOrderSettings.cutoffs).filter(
      (key) => accessCutoffDay(preOrderSettings.cutoffs, key)['enabled']
    )
    const day = dayjs(date).format('dddd').toLowerCase()
    return operatingDays.includes(day)
  }
  return false
}

const accessCutoffDay = (cutoffs: CutoffSchedule, day: string) => {
  switch (day) {
    case 'monday':
      return cutoffs.monday
    case 'tuesday':
      return cutoffs.tuesday
    case 'wednesday':
      return cutoffs.wednesday
    case 'thursday':
      return cutoffs.thursday
    case 'friday':
      return cutoffs.friday
    case 'saturday':
      return cutoffs.saturday
    case 'sunday':
      return cutoffs.sunday
    default:
      return {
        cutoff_date: null,
        enabled: false
      }
  }
}

export const accessOperatingDay = (
  operatingSchedules: OperatingSchedules,
  day: keyof OperatingSchedules
) => {
  if (operatingSchedules[day]) return operatingSchedules[day]
  return {
    enabled: false,
    start: null,
    end: null
  }
}

/*
  Moved this variable here since it's being used by
  the three generate* functions below.
*/
const ASAP_OFFSET = 45

export const generatePov3Timeslots = (
  store: Partial<StoreType>,
  fulfillmentDate: Date,
  currentDate: Date,
  timezone = 'Europe/London'
) => {
  const settings = store.pre_order_settings?.length
    ? store.pre_order_settings[0]
    : null
  if (!settings || !settings.delivery_schedule) return []
  const day = formatFulfillmentDay(fulfillmentDate, timezone)
  return timeslotsFromSchedule(
    settings.delivery_schedule as OperatingSchedules,
    day,
    settings.delivery_interval,
    !!settings.fixed_delivery_times_enabled,
    fulfillmentDate,
    currentDate,
    timezone
  )
}

export const generatePreorderSettingsTimeslots = (
  preOrderSettings: PreOrderSettings,
  fulfillmentDate: Date,
  currentDate: Date,
  timezone = 'Europe/London'
) => {
  const { delivery_schedule, delivery_interval, fixed_delivery_times_enabled } =
    preOrderSettings
  if (!delivery_schedule) return []
  const day = formatFulfillmentDay(fulfillmentDate, timezone)
  return timeslotsFromSchedule(
    delivery_schedule,
    day,
    delivery_interval,
    !!fixed_delivery_times_enabled,
    fulfillmentDate,
    currentDate,
    timezone
  )
}

const formatFulfillmentDay = (
  fulfillmentDate: Date,
  timezone = 'Europe/London'
) =>
  dayjs
    .tz(fulfillmentDate, timezone)
    .format('dddd')
    .toLowerCase() as keyof OperatingSchedules

const buildOpeningHourTimeslot = (
  start: string,
  end: string,
  fulfillmentDate: Date,
  timezone = 'Europe/London'
): Timeslot | null => {
  const [startHour, startMinute] = start.slice(0, 5).split(':')
  if (!startHour || !startMinute) return null
  const value = dayjs
    .tz(fulfillmentDate, timezone)
    .set('hour', parseInt(startHour, 10))
    .set('minute', parseInt(startMinute, 10))
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString()
  return { value, range: `${start.slice(0, 5)} - ${end.slice(0, 5)}` }
}

const timeslotsFromSchedule = (
  schedules: OperatingSchedules,
  day: keyof OperatingSchedules,
  interval: number | undefined,
  fixedTimesEnabled: boolean,
  fulfillmentDate: Date,
  currentDate: Date,
  timezone: string
): Timeslot[] => {
  const { start, end, enabled } = accessOperatingDay(schedules, day)
  if (!enabled || !start || !end) return []
  if (fixedTimesEnabled) {
    const slot = buildOpeningHourTimeslot(start, end, fulfillmentDate, timezone)
    return slot ? [slot] : []
  }
  if (interval == null) return []
  return createTimeIntervals(
    start,
    end,
    interval,
    fulfillmentDate,
    ASAP_OFFSET,
    currentDate,
    timezone
  )
}

const createTimeIntervals = (
  start: string | null,
  end: string | null,
  interval: number,
  selectedDate: Date,
  asapWindow: number,
  currentDate: Date,
  timezone = 'Europe/London'
) => {
  const [startHour, startMinutes] = start ? start.split(':') : [null, null]
  const [endHour, endMinutes] = end ? end.split(':') : [null, null]

  const selectedUTCDate = dayjs
    .tz(selectedDate, timezone)
    .format(DATE_FORMAT)
    .toString()
  // Store Opening time
  const openingTime =
    startHour && startMinutes
      ? dayjs
          .tz(selectedUTCDate, DATE_FORMAT, timezone)
          .set('minute', parseInt(startMinutes, 10))
          .set('hour', parseInt(startHour, 10))
          .toDate()
      : null

  // Store closing time
  const closingTime =
    endHour && endMinutes
      ? dayjs
          .tz(selectedUTCDate, DATE_FORMAT, timezone)
          .set('minute', parseInt(endMinutes, 10))
          .set('hour', parseInt(endHour, 10))
          .toDate()
      : null

  const startTime = getClosestTime(
    openingTime,
    selectedDate,
    interval,
    asapWindow,
    currentDate,
    timezone
  )
  const intervals: Timeslot[] = []
  let referenceTime = dayjs.tz(startTime, timezone)

  while (
    referenceTime &&
    closingTime &&
    referenceTime
      .second(0)
      .millisecond(0)
      .isBefore(dayjs(closingTime).second(0).millisecond(0))
  ) {
    const value = referenceTime.toISOString()
    const nextReference = referenceTime.clone().add(interval, 'minutes')
    const range = `${referenceTime.format('HH:mm').toString()} - ${nextReference
      .format('HH:mm')
      .toString()}`

    intervals.push({ value, range })

    referenceTime = nextReference
  }

  return intervals
}

const getClosestTime = (
  openingTime: Date | null,
  fulfillmentDate: Date,
  interval: number,
  asapWindow: number,
  currentDate: Date,
  timezone = 'Europe/London'
) => {
  const startOfDate = dayjs.tz(fulfillmentDate, timezone).startOf('day')
  const offsetOpening = dayjs
    .tz(openingTime, timezone)
    .add(-asapWindow, 'minutes')

  const startTime =
    openingTime &&
    dayjs
      .tz(startOfDate.toDate(), timezone)
      .isSame(dayjs.tz(openingTime, timezone), 'day') &&
    dayjs.tz(currentDate, timezone).isAfter(offsetOpening)
      ? getNextTime(interval, currentDate, asapWindow)
      : openingTime

  return startTime
}

const getNextTime = (
  interval: number,
  currentDate: Date,
  asapWindow: number
) => {
  const earliestDateTime = dayjs(currentDate).add(asapWindow, 'minutes')

  const minutes = earliestDateTime.minute()
  const addedMinutes = Math.ceil(minutes / interval) * interval
  return earliestDateTime.startOf('hour').add(addedMinutes, 'minutes').toDate()
}
